import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import savedCampaignService from '../../services/savedCampaignService';
import analyticsService from '../../services/analyticsService';
import { isCampaignActive } from '../../services/campaignService';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Icons
import {
  Bookmark,
  Star,
  SlidersHorizontal,
  X,
  Clock,
  TrendingUp,
  DollarSign,
  Users,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Camera,
  Video,
  Globe,
  Smartphone,
  CheckCircle2,
  Calendar,
  Sparkles,
  LayoutGrid,
  List,
} from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

// --- Helpers ---

const formatDate = (timestamp) => {
  if (!timestamp) return 'TBD';
  try {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'TBD';
  }
};

const formatPeso = (amount) => {
  const cleaned = (amount || 0).toString().replace(/[₱,\s]/g, '');
  const num = parseInt(cleaned, 10);
  if (isNaN(num)) return '₱0';
  return `₱${num.toLocaleString('en-US')}`;
};

const getCompensationLabel = (campaign) => {
  if (campaign.compensationType === 'monetary') {
    return formatPeso(campaign.compensationAmount || campaign.budget);
  }
  if (campaign.compensationType === 'product') {
    return campaign.productDetails || 'Product';
  }
  return `${formatPeso(campaign.compensationAmount || campaign.budget)} + Product`;
};

const getDaysUntil = (timestamp) => {
  if (!timestamp) return null;
  try {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

const getPlatformIcon = (platform) => {
  const p = platform?.toLowerCase();
  if (p === 'instagram') return Camera;
  if (p === 'youtube') return Video;
  if (p === 'tiktok') return Smartphone;
  if (p === 'twitch') return Video;
  if (p === 'facebook') return Globe;
  if (p === 'twitter' || p === 'x') return Globe;
  return Globe;
};

const isNewCampaign = (campaign) => {
  if (!campaign.createdAt) return false;
  try {
    const created = campaign.createdAt?.toDate ? campaign.createdAt.toDate() : new Date(campaign.createdAt);
    const diff = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    return diff <= 7;
  } catch {
    return false;
  }
};

// --- Component ---

const Campaigns = () => {
  const { currentUser } = useAuth();
  const { showError } = useFeedback();
  const [campaigns, setCampaigns] = useState([]);
  const [savedCampaigns, setSavedCampaigns] = useState([]);
  const [savingId, setSavingId] = useState(null); // campaignId currently being toggled
  const [applicationStatuses, setApplicationStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    compensationType: '',
    brand: '',
    status: 'active',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [savedOnly, setSavedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const itemsPerPage = 9;

  const [brandData, setBrandData] = useState({});

  // --- Data fetching (runs once on mount) ---

  const fetchCampaigns = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch campaigns — query by status so the filter actually works
      const campaignsRef = collection(db, 'campaigns');
      let q = query(campaignsRef, where('status', '==', filters.status || 'active'));
      q = query(q, orderBy('createdAt', 'desc'));

      const querySnapshot = await getDocs(q);

      const campaignsData = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const campaign = {
          id: docSnap.id,
          ...data,
          metrics: data.metrics || {},
        };
        if (!campaign.metrics.approvedReviews && data.analytics?.approvedContent) {
          campaign.metrics.approvedReviews = data.analytics.approvedContent;
        }
        campaignsData.push(campaign);
      });

      // Fetch saved campaign IDs and application statuses in parallel
      const [savedIds, appsSnapshot] = await Promise.all([
        savedCampaignService.getSavedCampaignIds(currentUser.uid),
        getDocs(query(
          collection(db, 'campaignParticipants'),
          where('influencerId', '==', currentUser.uid)
        )),
      ]);

      setSavedCampaigns(savedIds);

      // Build application status map: { campaignId: status }
      const appMap = {};
      appsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.campaignId) {
          appMap[data.campaignId] = data.status || 'applied';
        }
      });
      setApplicationStatuses(appMap);

      // Fetch brand data in parallel (fixes N+1 problem)
      const userService = (await import('../../services/userService')).default;
      const uniqueBrandIds = [...new Set(campaignsData.map((c) => c.brandId).filter(Boolean))];

      const brandResults = await Promise.all(
        uniqueBrandIds.map(async (brandId) => {
          try {
            const brandUser = await userService.getUserById(brandId);
            if (brandUser) {
              return [brandId, {
                id: brandUser.uid || brandUser.id,
                name: brandUser.companyName || brandUser.displayName || 'Brand',
                logo: brandUser.logo || brandUser.profileImageUrl || brandUser.profileImage || '/assets/default-logo.png',
              }];
            }
          } catch {
            // skip failed brand fetch
          }
          return null;
        })
      );

      const brands = {};
      brandResults.forEach((result) => {
        if (result) brands[result[0]] = result[1];
      });

      setBrandData(brands);
      setCampaigns(campaignsData);

      // Track analytics
      analyticsService.trackUserEvent(currentUser.uid, 'view_campaign_listing', {
        timestamp: new Date().toISOString(),
        filters,
      });
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, filters.status]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // --- Handlers ---

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({ category: '', compensationType: '', brand: '', status: 'active' });
    setSearchTerm('');
    setSavedOnly(false);
    setSortBy('newest');
    setCurrentPage(1);
  };

  const toggleSaveCampaign = async (campaignId) => {
    if (!currentUser || savingId === campaignId) return;

    const wasSaved = savedCampaigns.includes(campaignId);

    // Optimistic update — flip icon immediately
    setSavedCampaigns((prev) =>
      wasSaved ? prev.filter((id) => id !== campaignId) : [...prev, campaignId]
    );
    setSavingId(campaignId);

    try {
      await savedCampaignService.toggleSavedCampaign(currentUser.uid, campaignId, wasSaved);

      analyticsService.trackUserEvent(currentUser.uid, wasSaved ? 'unsave_campaign' : 'save_campaign', {
        campaignId,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      // Rollback on error
      setSavedCampaigns((prev) =>
        wasSaved ? [...prev, campaignId] : prev.filter((id) => id !== campaignId)
      );
      showError(
        'Could not save campaign',
        'Something went wrong. Please try again.',
        3000
      );
    } finally {
      setSavingId(null);
    }
  };

  // --- Derived data ---

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.category && filters.category !== 'all') count++;
    if (filters.compensationType && filters.compensationType !== 'all') count++;
    if (filters.brand && filters.brand !== 'all') count++;
    if (filters.status && filters.status !== 'active') count++;
    if (savedOnly) count++;
    if (searchTerm) count++;
    return count;
  }, [filters, savedOnly, searchTerm]);

  const filteredAndSortedCampaigns = useMemo(() => {
    let result = campaigns.filter((campaign) => {
      const brand = brandData[campaign.brandId] || { name: 'Unknown Brand' };

      const matchesSearch =
        campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = !filters.category || filters.category === 'all' || campaign.category === filters.category;
      const matchesCompensation = !filters.compensationType || filters.compensationType === 'all' || campaign.compensationType === filters.compensationType;
      const matchesBrand = !filters.brand || filters.brand === 'all' || campaign.brandId === filters.brand;
      const matchesStatus = !filters.status || campaign.status === filters.status;
      const matchesSaved = !savedOnly || savedCampaigns.includes(campaign.id);

      return matchesSearch && matchesCategory && matchesCompensation && matchesBrand && matchesStatus && matchesSaved;
    });

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'deadline':
          return (getDaysUntil(a.applicationDeadline || a.endDate) ?? Infinity) - (getDaysUntil(b.applicationDeadline || b.endDate) ?? Infinity);
        case 'compensation':
          return (parseInt((b.compensationAmount || b.budget || '0').toString().replace(/\D/g, ''), 10) || 0) - (parseInt((a.compensationAmount || a.budget || '0').toString().replace(/\D/g, ''), 10) || 0);
        case 'spots':
          return (Math.max(0, (a.totalSpots || 0) - (a.filledSpots || 0))) - (Math.max(0, (b.totalSpots || 0) - (b.filledSpots || 0)));
        case 'newest':
        default:
          return 0; // already sorted by createdAt desc from Firestore
      }
    });

    return result;
  }, [campaigns, brandData, filters, savedOnly, savedCampaigns, searchTerm, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedCampaigns.length / itemsPerPage);
  const paginatedCampaigns = filteredAndSortedCampaigns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // --- Constants ---

  const categoryOptions = ['Fashion', 'Beauty', 'Lifestyle', 'Travel', 'Food', 'Fitness', 'Technology', 'Gaming'];
  const compensationOptions = [
    { value: 'monetary', label: 'Money Only' },
    { value: 'product', label: 'Product Only' },
    { value: 'product_and_monetary', label: 'Product + Money' },
  ];
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'completed', label: 'Completed' },
  ];
  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'deadline', label: 'Deadline Soon' },
    { value: 'compensation', label: 'Highest Compensation' },
    { value: 'spots', label: 'Most Spots Left' },
  ];

  // --- Render ---

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Available Campaigns</h2>
        <p className="text-gray-600">Discover review opportunities from brands</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-grow">
              <input
                type="text"
                className="block w-full px-4 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Search campaigns by name, brand or description"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            {/* Sort dropdown */}
            <div className="sm:w-48">
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filters toggle with badge */}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`relative inline-flex items-center justify-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${
                showFilters || activeFilterCount > 0
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="h-4 w-4 mr-1.5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Saved Only toggle + Clear filters */}
          {(activeFilterCount > 0 || savedOnly) && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => { setSavedOnly(!savedOnly); setCurrentPage(1); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  savedOnly
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Bookmark className="h-4 w-4" />
                Saved Only
              </button>
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
                Clear all filters
              </button>
            </div>
          )}

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <Select
                  value={filters.category}
                  onValueChange={(value) => handleFilterChange('category', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categoryOptions.map((category) => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Compensation</label>
                <Select
                  value={filters.compensationType}
                  onValueChange={(value) => handleFilterChange('compensationType', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {compensationOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                <Select
                  value={filters.brand}
                  onValueChange={(value) => handleFilterChange('brand', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {Object.values(brandData)
                      .filter((brand, index, self) => self.findIndex((b) => b.id === brand.id) === index)
                      .map((brand) => (
                        <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select
                  value={filters.status}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="flex justify-center mb-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={fetchCampaigns}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </button>
        </div>
      )}

      {/* Results */}
      {!loading && !error && (
        <>
          {/* Results count + View toggle */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing <span className="font-medium text-gray-700">{paginatedCampaigns.length}</span> of{' '}
              <span className="font-medium text-gray-700">{filteredAndSortedCampaigns.length}</span> campaigns
            </p>
            <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`inline-flex items-center justify-center w-8 h-8 rounded transition-colors ${
                  viewMode === 'grid' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                aria-label="Grid view"
                title="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`inline-flex items-center justify-center w-8 h-8 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                aria-label="List view"
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Campaign Cards */}
          {paginatedCampaigns.length > 0 ? (
            viewMode === 'grid' ? (
              /* ---- GRID VIEW (compact cards) ---- */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {paginatedCampaigns.map((campaign) => {
                  const spotsLeft = Math.max(0, (campaign.totalSpots || 0) - (campaign.filledSpots || 0));
                  const daysLeft = getDaysUntil(campaign.applicationDeadline || campaign.endDate);
                  const appStatus = applicationStatuses[campaign.id];
                  const isNew = isNewCampaign(campaign);

                  return (
                    <div
                      key={campaign.id}
                      className="bg-white rounded-lg shadow-md border border-gray-100 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                    >
                      {/* Campaign Image or placeholder */}
                      <div className="w-full h-52 overflow-hidden rounded-t-lg relative bg-gray-100">
                        {campaign.imageUrl ? (
                          <img
                            src={campaign.imageUrl}
                            alt={campaign.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Sparkles className="h-8 w-8 text-primary/30" />
                          </div>
                        )}
                        {/* New badge */}
                        {isNew && (
                          <span className="absolute top-2 left-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold text-white bg-primary shadow-sm">
                            NEW
                          </span>
                        )}
                        {/* Applied badge */}
                        {appStatus && (
                          <span className={`absolute top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium shadow-sm ${
                            appStatus === 'approved' ? 'bg-green-500 text-white' :
                            appStatus === 'rejected' ? 'bg-red-500 text-white' :
                            'bg-white text-gray-700'
                          }`}>
                            <CheckCircle2 className="h-3 w-3" />
                            {appStatus === 'approved' ? 'Approved' : appStatus === 'rejected' ? 'Rejected' : 'Applied'}
                          </span>
                        )}
                      </div>

                      <div className="p-4 flex-1 flex flex-col">
                        {/* Title + bookmark */}
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-base font-semibold text-gray-900 line-clamp-1 flex-1 min-w-0">{campaign.name}</h3>
                          <button
                            onClick={() => toggleSaveCampaign(campaign.id)}
                            disabled={savingId === campaign.id}
                            className="text-gray-400 hover:text-primary ml-2 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={savedCampaigns.includes(campaign.id) ? 'Remove from saved' : 'Save campaign'}
                            title={savedCampaigns.includes(campaign.id) ? 'Remove from saved' : 'Save campaign'}
                          >
                            {savingId === campaign.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : savedCampaigns.includes(campaign.id) ? (
                              <Bookmark className="h-4 w-4 text-primary" fill="currentColor" />
                            ) : (
                              <Bookmark className="h-4 w-4" />
                            )}
                          </button>
                        </div>

                        {/* Brand */}
                        <div className="flex items-center text-sm text-gray-500 mb-2">
                          {brandData[campaign.brandId] && (
                            <>
                              <img
                                src={brandData[campaign.brandId].logo}
                                alt={brandData[campaign.brandId].name}
                                className="w-4 h-4 rounded-full mr-1.5 object-cover"
                                onError={(e) => { e.target.src = '/assets/default-logo.png'; }}
                              />
                              <span className="truncate">{brandData[campaign.brandId].name}</span>
                            </>
                          )}
                        </div>

                        {/* Category + status badges */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {campaign.category}
                          </span>
                          {isCampaignActive(campaign) ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Ended
                            </span>
                          )}
                        </div>

                        {/* Urgency indicator (only if urgent) */}
                        {(daysLeft !== null && daysLeft <= 3 && daysLeft >= 0) && (
                          <div className="mb-3 flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-md">
                            <Clock className="h-3.5 w-3.5" />
                            {daysLeft === 0 ? 'Deadline is today!' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left to apply`}
                          </div>
                        )}
                        {spotsLeft > 0 && spotsLeft <= 3 && (
                          <div className="mb-3 flex items-center gap-1.5 text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-md">
                            <Users className="h-3.5 w-3.5" />
                            Only {spotsLeft} spot{spotsLeft === 1 ? '' : 's'} left!
                          </div>
                        )}

                        {/* Compact info row */}
                        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                          <div>
                            <span className="text-gray-500">Compensation:</span>{' '}
                            <span className="font-semibold text-gray-900">{getCompensationLabel(campaign)}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-gray-500 text-xs block">Spots Left</span>
                            <span className="font-semibold text-gray-900">{spotsLeft}</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-3 bg-gray-50 border-t border-gray-200">
                        <Link
                          to={`/influencer/campaigns/${campaign.id}`}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ---- LIST VIEW (compact rows) ---- */
              <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                {/* Table header (desktop only) */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="col-span-4">Campaign</div>
                  <div className="col-span-2">Category</div>
                  <div className="col-span-2">Compensation</div>
                  <div className="col-span-2">Deadline</div>
                  <div className="col-span-1 text-center">Spots</div>
                  <div className="col-span-1 text-right">Action</div>
                </div>

                {paginatedCampaigns.map((campaign) => {
                  const spotsLeft = Math.max(0, (campaign.totalSpots || 0) - (campaign.filledSpots || 0));
                  const daysLeft = getDaysUntil(campaign.applicationDeadline || campaign.endDate);
                  const appStatus = applicationStatuses[campaign.id];
                  const isNew = isNewCampaign(campaign);

                  return (
                    <div
                      key={campaign.id}
                      className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors items-center"
                    >
                      {/* Campaign name + brand */}
                      <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary/10 to-blue-400/10">
                          {campaign.imageUrl ? (
                            <img src={campaign.imageUrl} alt={campaign.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Sparkles className="h-5 w-5 text-primary/30" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-900 truncate">{campaign.name}</h3>
                            {isNew && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold text-white bg-primary">
                                NEW
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-xs text-gray-500 mt-0.5">
                            {brandData[campaign.brandId] && (
                              <>
                                <img
                                  src={brandData[campaign.brandId].logo}
                                  alt={brandData[campaign.brandId].name}
                                  className="w-3.5 h-3.5 rounded-full mr-1 object-cover"
                                  onError={(e) => { e.target.src = '/assets/default-logo.png'; }}
                                />
                                <span className="truncate">{brandData[campaign.brandId].name}</span>
                              </>
                            )}
                            {appStatus && (
                              <span className={`ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                appStatus === 'approved' ? 'bg-green-100 text-green-700' :
                                appStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                <CheckCircle2 className="h-2.5 w-2.5" />
                                {appStatus === 'approved' ? 'Approved' : appStatus === 'rejected' ? 'Rejected' : 'Applied'}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Bookmark */}
                        <button
                          onClick={() => toggleSaveCampaign(campaign.id)}
                          disabled={savingId === campaign.id}
                          className="text-gray-400 hover:text-primary flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={savedCampaigns.includes(campaign.id) ? 'Remove from saved' : 'Save campaign'}
                          title={savedCampaigns.includes(campaign.id) ? 'Remove from saved' : 'Save campaign'}
                        >
                          {savingId === campaign.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : savedCampaigns.includes(campaign.id) ? (
                            <Bookmark className="h-4 w-4 text-primary" fill="currentColor" />
                          ) : (
                            <Bookmark className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {/* Category */}
                      <div className="md:col-span-2 flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {campaign.category}
                        </span>
                        {!isCampaignActive(campaign) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Ended
                          </span>
                        )}
                      </div>

                      {/* Compensation */}
                      <div className="md:col-span-2 text-sm font-semibold text-gray-900">
                        {getCompensationLabel(campaign)}
                      </div>

                      {/* Deadline */}
                      <div className="md:col-span-2 text-sm text-gray-600">
                        <div>{formatDate(campaign.applicationDeadline || campaign.endDate)}</div>
                        {(daysLeft !== null && daysLeft <= 3 && daysLeft >= 0) && (
                          <div className="text-xs font-medium text-orange-600 flex items-center gap-1 mt-0.5">
                            <Clock className="h-3 w-3" />
                            {daysLeft === 0 ? 'Today!' : `${daysLeft}d left`}
                          </div>
                        )}
                      </div>

                      {/* Spots */}
                      <div className="md:col-span-1 text-center">
                        <span className={`text-sm font-medium ${
                          spotsLeft > 0 && spotsLeft <= 3 ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {spotsLeft}
                        </span>
                      </div>

                      {/* Action */}
                      <div className="md:col-span-1 text-right">
                        <Link
                          to={`/influencer/campaigns/${campaign.id}`}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
                        >
                          View
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-4">
                <Star className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {campaigns.length === 0 ? 'No campaigns available' : 'No campaigns found'}
              </h3>
              <p className="text-gray-500 mb-4">
                {campaigns.length === 0
                  ? 'There are no campaigns available right now. Check back soon for new opportunities!'
                  : 'No campaigns match your current filters. Try adjusting your search criteria.'}
              </p>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <X className="h-4 w-4" />
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-primary text-white border border-primary'
                      : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Campaigns;
